import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  Grid,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { alpha, styled } from '@mui/material/styles';
import { closetAPI } from '../utils/api';
import ClosetLimits from '../components/Layout/ClosetLimits';
import DetectedItemsList from '../components/DetectedItemsList';
import { debugLog } from '../utils/debug';
import { categories } from '../utils/constants';
import { bulkAddClosetItems } from '../utils/bulkAdd';

// Styled components
const ImagePreview = styled('img')(({ theme }) => ({
  width: '100%',
  height: 'auto',
  maxHeight: '300px',
  objectFit: 'contain',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  borderRadius: theme.spacing(1),
  border: `1px dashed ${theme.palette.grey[400]}`,
  padding: theme.spacing(1),
  backgroundColor: alpha(theme.palette.primary.main, 0.03),
  [theme.breakpoints.down('sm')]: {
    maxHeight: '200px',
  },
}));

const AddItem = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    color: '',
  });
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [errors, setErrors] = useState({});
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
  const [uploadedImageUrl, setUploadedImageUrl] = useState('');
  const [detectedItems, setDetectedItems] = useState([]);

  // Track mounted state to prevent memory leaks
  const isMountedRef = useRef(true);
  const fileReaderRef = useRef(null);
  const abortControllerRef = useRef(null);
  const timeoutIdRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    const fileReader = fileReaderRef;
    const abortController = abortControllerRef;
    const timeoutId = timeoutIdRef;
    return () => {
      isMountedRef.current = false;

      // Abort FileReader if in progress
      if (fileReader.current) {
        fileReader.current.abort();
      }

      // Abort any pending fetch requests
      if (abortController.current) {
        abortController.current.abort();
      }

      // Clear timeout
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
      }
    };
  }, []);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear any errors for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  // Handle file upload
  const handleFileChange = async (e) => {
    const uploadedFile = e.target.files[0];
    if (uploadedFile) {
      setFile(uploadedFile);
      // Create preview URL with proper cleanup
      const reader = new FileReader();
      fileReaderRef.current = reader;

      reader.onloadend = () => {
        if (isMountedRef.current) {
          setPreviewUrl(reader.result);
        }
      };

      reader.onerror = () => {
        if (isMountedRef.current) {
          console.error('FileReader error:', reader.error);
        }
      };

      reader.readAsDataURL(uploadedFile);

      // Reset any previously analyzed data
      setUploadedImageUrl('');
    }
  };

  // Analyze the uploaded image
  const handleAnalyzeImage = async () => {
    if (!file) {
      setAlert({
        open: true,
        message: 'Please upload an image first',
        severity: 'warning'
      });
      return;
    }
    
    setAnalyzing(true);
    try {
      debugLog("Starting image analysis process...");
      
      // Create form data with the file
      const formData = new FormData();
      formData.append('file', file);
      debugLog("Sending image for analysis:", file.name);
      
      // Send the file directly to the analyze endpoint
      const analysisResponse = await closetAPI.analyzeImageFile(formData);
      debugLog("Analysis response:", analysisResponse.data);
      
      const analysisData = analysisResponse.data.data;
      
      debugLog("Analysis data:", analysisData);
      
      // Upload the image for storage first
      const uploadResponse = await closetAPI.uploadImage(formData);
      const imageUrl = uploadResponse.data.url;
      debugLog("Image uploaded successfully, URL:", imageUrl);
      setUploadedImageUrl(imageUrl);
      
      // Handle single vs multiple items
      if (analysisData.items_found === 1) {
        // Single item - update form data as before
        const item = analysisData.items[0];
        setFormData(prev => ({
          ...prev,
          description: item.description || prev.description,
          category: item.category || prev.category,
          color: item.color || prev.color,
        }));
        
        setAlert({
          open: true,
          message: 'Single item analyzed successfully!',
          severity: 'success'
        });
      } else if (analysisData.items_found > 1) {
        // Multiple items detected
        const itemsWithImage = analysisData.items.map(item => ({
          ...item,
          image_url: imageUrl,
          name: `${item.type || item.category || 'Item'}` // Auto-generate name
        }));
        
        setDetectedItems(itemsWithImage);
        
        setAlert({
          open: true,
          message: `${analysisData.items_found} items detected! Review and add them to your closet.`,
          severity: 'success'
        });
      } else {
        // No items found
        setAlert({
          open: true,
          message: 'No clothing items detected in the image. Please try a different image.',
          severity: 'warning'
        });
      }
    } catch (err) {
      console.error('Error analyzing image:', err);
      // Log more detailed error information
      if (err.response) {
        console.error('Error response data:', err.response.data);
        console.error('Error response status:', err.response.status);
      }
      
      setAlert({
        open: true,
        message: 'Failed to analyze image. Please try again or fill details manually.',
        severity: 'error'
      });
    } finally {
      setAnalyzing(false);
    }
  };

  // Validate the form
  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.category) newErrors.category = 'Category is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      // Use already uploaded image if available, otherwise upload it
      let imageUrl = uploadedImageUrl;
      if (!imageUrl && file) {
        const formData = new FormData();
        formData.append('file', file);
        const response = await closetAPI.uploadImage(formData);
        imageUrl = response.data.url;
      }
      
      // Then add the item with the image URL
      await closetAPI.addItem({
        category: formData.category,
        color: formData.color,
        description: formData.description,
        // Use name as a custom field if needed
        name: formData.name,
        image_url: imageUrl
      });
      
      setAlert({
        open: true,
        message: 'Item added successfully!',
        severity: 'success'
      });
      
      // Navigate back to closet after a short delay
      setTimeout(() => {
        navigate('/closet');
      }, 1500);
    } catch (err) {
      console.error('Error adding item:', err);
      setAlert({
        open: true,
        message: 'Failed to add item. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle multiple items detected - bulk add
  const handleBulkAdd = async () => {
    setLoading(true);
    try {
      const result = await bulkAddClosetItems(detectedItems, abortControllerRef, timeoutIdRef);

      let message = `Successfully added ${result.items_added} items to your closet!`;
      if (result.errors && result.errors.length > 0) {
        message += `\n\nSome items failed to add:\n${result.errors.join('\n')}`;
      }

      setAlert({
        open: true,
        message,
        severity: result.errors && result.errors.length > 0 ? 'warning' : 'success'
      });

      setTimeout(() => {
        navigate('/closet');
      }, 2000);
    } catch (err) {
      console.error('Error adding multiple items:', err);

      let errorMessage = 'Failed to add items. Please try again.';
      if (err.name === 'AbortError') {
        errorMessage = 'Request timed out. Please try with fewer items or check your connection.';
      } else if (err.message.includes('Failed to fetch')) {
        errorMessage = 'Network error. Please check your internet connection and try again.';
      } else if (err.message.includes('JSON')) {
        errorMessage = 'Server response error. Please try again or contact support if the problem persists.';
      }

      setAlert({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle editing detected items
  const handleEditDetectedItem = (index, field, value) => {
    setDetectedItems(prev => 
      prev.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    );
  };

  // Handle removing detected items
  const handleRemoveDetectedItem = (index) => {
    setDetectedItems(prev => prev.filter((_, i) => i !== index));
  };

  // Handle alert close
  const handleAlertClose = () => {
    setAlert((prev) => ({ ...prev, open: false }));
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          borderRadius: 3,
          background:
            'radial-gradient(280px 160px at 90% -20%, rgba(255, 110, 77, 0.2), transparent 70%), linear-gradient(135deg, rgba(14, 123, 255, 0.14), rgba(0, 165, 255, 0.08))',
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom>
          Add New Item
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Upload a clothing image, auto-detect details with AI, and save items to your closet.
        </Typography>
        <ClosetLimits variant="compact" />
      </Paper>

      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Image Upload Section */}
            <Grid item xs={12}>
              <Box sx={{ 
                border: '2px dashed',
                borderColor: 'divider',
                borderRadius: 3,
                p: 3,
                textAlign: 'center',
                bgcolor: 'rgba(255, 255, 255, 0.82)',
                cursor: 'pointer',
                transition: 'transform 0.2s ease, border-color 0.2s ease, background-color 0.2s ease',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'rgba(14, 123, 255, 0.06)',
                  transform: 'translateY(-2px)',
                },
              }}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  id="image-upload"
                />
                <label htmlFor="image-upload">
                  <Box sx={{ cursor: 'pointer' }}>
                    <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                      {file ? 'Change Image' : 'Upload Image'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Click to upload or drag and drop
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      PNG, JPG, GIF up to 5MB
                    </Typography>
                  </Box>
                </label>
              </Box>
            </Grid>

            {/* Image Preview */}
            {previewUrl && (
              <Grid item xs={12}>
                <ImagePreview src={previewUrl} alt="Preview" />
              </Grid>
            )}
            {!previewUrl && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    borderRadius: 2,
                    border: '1px dashed',
                    borderColor: 'divider',
                    p: 2,
                    textAlign: 'center',
                    color: 'text.secondary',
                    fontSize: '0.9rem',
                    backgroundColor: 'rgba(255,255,255,0.55)',
                  }}
                >
                  Upload an image to preview and analyze before adding.
                </Box>
              </Grid>
            )}

            {/* Analysis Button */}
            <Grid item xs={12}>
              <Button
                variant="contained"
                color="secondary"
                startIcon={analyzing ? <CircularProgress size={20} color="inherit" /> : <AutoFixHighIcon />}
                onClick={handleAnalyzeImage}
                disabled={!file || loading || analyzing}
                fullWidth
                sx={{ 
                  py: 1.5,
                  minHeight: '48px',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  borderRadius: 3,
                }}
              >
                {analyzing ? 'Analyzing...' : 'Analyze with AI'}
              </Button>
            </Grid>

            {/* Form Fields */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                error={!!errors.name}
                helperText={errors.name}
                sx={{ mb: 2 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.category}>
                <InputLabel>Category</InputLabel>
                <Select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  label="Category"
                >
                  {categories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
                {errors.category && (
                  <FormHelperText>{errors.category}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Color"
                name="color"
                value={formData.color}
                onChange={handleChange}
                error={!!errors.color}
                helperText={errors.color}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                multiline
                rows={4}
                error={!!errors.description}
                helperText={errors.description}
              />
            </Grid>

            {/* Multiple Items Detected Section */}
            {detectedItems.length > 0 && (
              <Grid item xs={12}>
                <DetectedItemsList
                  items={detectedItems}
                  onEditItem={handleEditDetectedItem}
                  onRemoveItem={handleRemoveDetectedItem}
                  onBulkAdd={handleBulkAdd}
                  loading={loading}
                />
              </Grid>
            )}

            {/* Submit Button (for single items) */}
            {detectedItems.length === 0 && (
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                  fullWidth
                  sx={{ 
                    py: 1.5,
                    minHeight: '48px',
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    borderRadius: 3,
                  }}
                >
                  {loading ? 'Adding Item...' : 'Add to Closet'}
                </Button>
              </Grid>
            )}
          </Grid>
        </form>
      </Paper>

      {/* Alert Snackbar */}
      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={handleAlertClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleAlertClose} 
          severity={alert.severity}
          sx={{ width: '100%' }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AddItem; 
